import { useCallback, useState, useEffect, useRef } from 'react';
import useEventCallback from '../useEventCallback';

var isRef = function isRef(ref) {
  return typeof ref !== "string";
};

var validSelector = function validSelector(slug) {
  var selector = slug.trim();

  if (selector && selector.match(/^\d/)) {
    selector = selector.replace(/^[0-9]+/, "");
    selector = selector.startsWith("-") ? selector.replace("-", "") : selector;
  }

  return selector;
};
/**
 * A hook to observe elements in the viewport
 * @param refs - An array of refs or selectors
 * @param callback - The function to call when an element is intersecting
 * @param options - The options for the IntersectionObserver
 * @returns An object containing the update function to update the observed elements and the observer reference
 */


var useIntersectionObserver = function useIntersectionObserver(refs, callback, options) {
  var observerRef = useRef();
  var currentlyObserved = useRef(Array.from({
    length: refs.length
  }).fill(null));

  var _useState = useState(0),
      updateCount = _useState[0],
      setUpdateCount = _useState[1];

  var callbackHandle = useEventCallback(callback, []);
  useEffect(function () {
    var intersectionObserver = new IntersectionObserver(callbackHandle, options);
    observerRef.current = intersectionObserver;
    refs.forEach(function (ref, index) {
      var selector = !isRef(ref) && validSelector(ref);
      var element = isRef(ref) ? ref.current : document.querySelector("#".concat(selector));

      if (element && currentlyObserved.current) {
        intersectionObserver.observe(element);
        currentlyObserved.current[index] = element;
      }
    });
    return function () {
      intersectionObserver.disconnect();
      currentlyObserved.current = null;
    };
  }, [updateCount, callbackHandle]);
  var update = useCallback(function () {
    return setUpdateCount(function (state) {
      return ++state;
    });
  }, []); // Update observed element
  // JSX is changing and observed element can be mounted and unmounted from jsx tree
  // This effect takes care of updating currently observed element to newest reference value

  useEffect(function () {
    refs.forEach(function (ref, index) {
      var selector = !isRef(ref) && validSelector(ref);
      var element = isRef(ref) ? ref.current : document.querySelector("#".concat(selector));

      if (observerRef.current && currentlyObserved.current) {
        var areDifferent = currentlyObserved.current[index] !== element;

        if (currentlyObserved.current[index] && areDifferent && isRef(ref)) {
          var _observerRef$current;

          (_observerRef$current = observerRef.current) === null || _observerRef$current === void 0 ? void 0 : _observerRef$current.unobserve(currentlyObserved.current[index]);
          currentlyObserved.current[index] = null;
        }

        if (areDifferent && element && isRef(ref)) {
          var _observerRef$current2;

          (_observerRef$current2 = observerRef.current) === null || _observerRef$current2 === void 0 ? void 0 : _observerRef$current2.observe(element);
          currentlyObserved.current[index] = element;
        }
      }
    });
  });
  return {
    observerRef: observerRef,
    update: update
  };
};

export default useIntersectionObserver;